import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import { PdBaseUIInput } from "../base/pd-base-ui-input.js";
import { css, html } from "lit";
import { property } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
//#region src/pd-range/PdRange.ts
/**
* @license
* Copyright (c) 2021 PD Progressive Development UG. All rights reserved.
*/
/**
* Range slider input component.
*
* @tagname pd-range
* @summary Range slider input with customizable min/max/step.
*
* @event validate-form - Fired when value changes for validation.
* @event field-change - Fired when value changes.
* @event pd-form-element-blur - Fired when the range input loses focus.
* @event pd-form-element-focus - Fired when the range input gains focus.
*
* @cssprop --pd-input-field-width - Slider width.
* @cssprop --pd-input-field-range-height - Slider height.
* @cssprop --pd-range-bg-col - Slider background.
* @cssprop --pd-range-thumb-col - Thumb color.
* @cssprop --pd-range-track-col - Track color.
* @cssprop --pd-range-track-hover-col - Track hover color.
*/
var PdRange = class extends PdBaseUIInput {
	static {
		this.styles = [PdBaseUIInput.styles, css`
      :host {
        display: inline-block;
        box-sizing: border-box;
      }

      :host([disabled]) {
        --pd-range-bg-col: var(--pd-default-disabled-col);
        --pd-range-thumb-col: var(--pd-default-disabled-light-col);
      }

      .input-range {
        display: inline-block;
        width: var(--pd-input-field-width, 250px);
        box-sizing: border-box;
      }

      input[type="range"] {
        -webkit-appearance: none;
        width: 100%;
        background: transparent;
        background-color: var(--pd-range-bg-col, var(--pd-default-col));
        padding: 0.5rem;
        height: var(--pd-input-field-height, 2.5rem);
        box-sizing: border-box;
        border-radius: 0.75rem;
      }

      input[type="range"]:focus {
        outline: none;
      }

      input[type="range"]:focus-visible {
        outline: 2px solid var(--pd-focus-ring-col);
        outline-offset: 2px;
      }

      input[type="range"]::-webkit-slider-thumb {
        -webkit-appearance: none;
        margin-top: -6px;
        cursor: pointer;
        height: 15px;
        width: 39px;
        background: var(--pd-range-thumb-col, var(--pd-default-hover-col));
        border: 1px solid var(--pd-default-dark-col);
        border-radius: 3px;
        box-shadow: var(--pd-shadow-sm);
      }

      input[type="range"]::-moz-range-thumb {
        cursor: pointer;
        height: 15px;
        width: 39px;
        background: var(--pd-range-thumb-col, var(--pd-default-hover-col));
        border: 1px solid var(--pd-default-dark-col);
        border-radius: 3px;
        box-shadow: var(--pd-shadow-sm);
      }

      input[type="range"]::-ms-thumb {
        cursor: pointer;
        height: 15px;
        width: 39px;
        background: var(--pd-range-thumb-col, var(--pd-default-hover-col));
        border: 1px solid var(--pd-default-dark-col);
        border-radius: 3px;
        box-shadow: var(--pd-shadow-sm);
      }

      input[type="range"]::-webkit-slider-runnable-track {
        width: 100%;
        height: 8.4px;
        cursor: pointer;
        box-shadow: var(--pd-shadow-sm);
        background: var(--pd-range-track-col, var(--pd-default-light-col));
        border: 0.2px solid var(--pd-default-darkest-col);
        border-radius: 0.75rem;
      }

      input[type="range"]:focus::-webkit-slider-runnable-track {
        background: var(
          --pd-range-track-hover-col,
          var(--pd-default-light-col)
        );
        border: 0.2px solid var(--pd-default-hover-col);
      }

      input[type="range"]::-moz-range-track {
        width: 100%;
        height: 8.4px;
        cursor: pointer;
        box-shadow: var(--pd-shadow-sm);
        background: var(--pd-range-track-col, var(--pd-default-light-col));
        border-radius: 0.75rem;
        border: 0.2px solid var(--pd-default-darkest-col);
      }

      input[type="range"]::-ms-track {
        height: 8.4px;
        width: 100%;
        cursor: pointer;
        background: transparent;
        border-color: transparent;
        color: transparent;
        border-width: 16px 0;
      }

      input[type="range"]::-ms-fill-lower,
      input[type="range"]::-ms-fill-upper {
        background: var(--pd-range-track-col, var(--pd-default-light-col));
        border: 0.2px solid var(--pd-default-darkest-col);
        border-radius: 0.75rem;
        box-shadow: var(--pd-shadow-sm);
      }

      input[type="range"]:focus::-ms-fill-lower {
        background: var(--pd-default-col);
      }

      input[type="range"]:focus::-ms-fill-upper {
        background: var(--pd-default-lighter-col);
      }

      .ticks {
        display: flex;
        justify-content: space-between;
        padding: 10px 20px;
        box-sizing: border-box;
      }

      .tick {
        line-height: 5px;
        height: 5px;
        margin-top: -13px;
        font-size: 8pt;
        font-weight: bolder;
      }

      .ticks.disabled {
        color: var(--pd-default-disabled-col);
      }

      .ticks.enabled {
        color: var(--pd-default-col);
      }

      @media (max-width: 650px) {
        label {
          font-size: 12px;
        }
      }
    `];
	}
	constructor() {
		super();
		this.min = 0;
		this.max = 10;
		this.step = 1;
		this.optionValueMapper = {};
		this.noTicks = false;
		this.name = "";
		this._inputType = 4;
	}
	update(changedProps) {
		if (changedProps.has("optionValueMapper") && !this.initValue) this._value = Object.keys(this.optionValueMapper)[0];
		super.update(changedProps);
	}
	firstUpdated() {
		this._input = this.shadowRoot?.querySelector("input");
		super.firstUpdated();
	}
	_onChange(e) {
		this._handleChangedValue(this._input?.value ?? "", e, true);
	}
	_onBlur(_event) {
		this._focused = false;
		this._touched = true;
		this.dispatchEvent(new CustomEvent("pd-form-element-blur", {
			detail: {
				name: this.valueName || this.id || "field",
				value: this._input?.value
			},
			bubbles: true,
			composed: true
		}));
	}
	_onFocus(_event) {
		this._focused = true;
		this.dispatchEvent(new CustomEvent("pd-form-element-focus", {
			detail: {
				name: this.valueName || this.id || "field",
				value: this._input?.value
			},
			bubbles: true,
			composed: true
		}));
	}
	focus() {
		this._input?.focus();
	}
	render() {
		const inputId = `${this.id}Range`;
		const labelDetail = this.optionValueMapper[this._value] || this._value;
		return html`
      ${this._renderLabel(inputId, labelDetail)}
      <div
        class="${classMap(this.getClassmap({ "input-range": true }))}"
      >
        <input
          id="${inputId}"
          name="${this.name || this.valueName || ""}"
          class="input-range-style ${this.gradient ? "gradient" : ""}"
          type="range"
          min="${this.min}"
          max="${this.max}"
          step="${this.step}"
          .value="${this._value}"
          ?disabled="${this.disabled}"
          aria-valuemin="${this.min}"
          aria-valuemax="${this.max}"
          aria-valuenow="${this._value}"
          aria-valuetext="${labelDetail}"
          @change="${this._onChange}"
          @input="${this._onChange}"
          @blur="${this._onBlur}"
          @focus="${this._onFocus}"
        />
        ${this.noTicks ? "" : html`<div
                class="ticks ${this.disabled ? "disabled" : "enabled"}"
                aria-hidden="true"
              >
                ${Array.from({ length: this.max - this.min + 1 }).map(() => html`<span class="tick">|</span>`)}
              </div>`}
      </div>
      ${this._renderErrorMsg()}
    `;
	}
	_getInitialValue() {
		return this.initValue || Object.keys(this.optionValueMapper)[0] || "0";
	}
	clear() {
		this.reset();
	}
};
__decorate([property({ type: Number })], PdRange.prototype, "min", void 0);
__decorate([property({ type: Number })], PdRange.prototype, "max", void 0);
__decorate([property({ type: Number })], PdRange.prototype, "step", void 0);
__decorate([property({ type: Object })], PdRange.prototype, "optionValueMapper", void 0);
__decorate([property({
	type: Boolean,
	attribute: "no-ticks"
})], PdRange.prototype, "noTicks", void 0);
__decorate([property({ type: String })], PdRange.prototype, "name", void 0);
//#endregion
export { PdRange };
